import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { DatePicker, formatPickerDate } from '@scripsteam/scrips-signal-ds';
import type { AppointmentFormValues } from '@/types/appointment-form';
import { practiceNow } from '@/shared/lib/practice-time';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Trigger + DS footer share this exact format so the two never drift. */
const DATE_FORMAT = 'MMMM dd, yyyy';

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getStripDays(anchor: Date) {
  return Array.from({ length: 5 }, (_, i) => {
    const d = startOfDay(anchor);
    d.setDate(anchor.getDate() + i);
    return d;
  });
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

/**
 * DatePickerSection — tappable "MMM dd, yyyy ▼" header + 5-day strip.
 * Mirrors Flutter no-provider-state date picker (create_appointment_widget.dart:816–911).
 * Slot lookup lives in TimeSlotsSection (post-profile).
 */
export function DatePickerSection() {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const selectedDate = watch('selectedDate');

  useEffect(() => {
    if (!selectedDate) {
      setValue('selectedDate', startOfDay(practiceNow()).toISOString(), { shouldValidate: true });
    }
  }, [selectedDate, setValue]);

  const today = startOfDay(practiceNow());
  const selectedDateObj = selectedDate ? new Date(selectedDate) : today;

  const [anchorDate, setAnchorDate] = useState(() => startOfDay(practiceNow()));
  const stripDays = getStripDays(anchorDate);

  // Re-anchor strip when selected date moves outside the visible window.
  useEffect(() => {
    if (!selectedDate) return;
    const sd = startOfDay(new Date(selectedDate));
    setAnchorDate((prev) => {
      const prevEnd = new Date(prev);
      prevEnd.setDate(prev.getDate() + 4);
      const startsBefore = sd.getTime() < prev.getTime();
      const endsAfter = sd.getTime() > prevEnd.getTime();
      return startsBefore || endsAfter ? sd : prev;
    });
  }, [selectedDate]);

  // Today is the floor — past dates aren't bookable. Flutter
  // (`create_appointment_widget.dart:828`) ships `firstDate =
  // DateTime.now().subtract(Duration(days: 1))` (yesterday) which
  // lets the user pick a past date by accident; we tighten to today.
  const minDate = startOfDay(practiceNow());

  const selectDay = (d: Date) => {
    if (startOfDay(d).getTime() < minDate.getTime()) return;
    setValue('selectedDate', startOfDay(d).toISOString(), { shouldValidate: true });
    setValue('slotId', null);
    setValue('slotStart', null);
    setValue('slotEnd', null);
  };

  const shiftStrip = (deltaDays: number) => {
    const d = new Date(anchorDate);
    d.setDate(d.getDate() + deltaDays);
    if (d.getTime() < minDate.getTime()) return;
    setAnchorDate(d);
  };

  return (
    <div className="booking-section booking-date" data-slot="booking-section">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => shiftStrip(-5)} className="icon-action" aria-label="Previous days">
          <ChevronLeft className="size-4" />
        </button>
        <DatePicker
          value={selectedDateObj}
          onChange={(d) => {
            if (d) selectDay(d);
          }}
          minDate={minDate}
          dateFormat={DATE_FORMAT}
          trigger={
            <button
              type="button"
              className="sched-month-btn"
            >
              <span>{formatPickerDate(selectedDateObj, DATE_FORMAT)}</span>
              <ChevronDown className="size-4" />
            </button>
          }
        />
        <button type="button" onClick={() => shiftStrip(5)} className="icon-action" aria-label="Next days">
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="grid grid-cols-5 gap-1">
        {stripDays.map((d) => {
          const isSelected = isSameDay(d, selectedDateObj);
          const isPast = d.getTime() < minDate.getTime();
          return (
            <button
              key={d.toISOString()}
              type="button"
              disabled={isPast}
              onClick={() => selectDay(d)}
              className="sched-day"
                    data-selected={isSelected || undefined}
                    data-off={isPast || undefined}
            >
              <span className="sched-day-meta">{MONTH_SHORT[d.getMonth()]}</span>
              <span className="sched-day-num">{d.getDate()}</span>
              <span className="sched-day-meta">{DAY_NAMES[d.getDay()]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
